// Magnetic Lasso Tool — follows edges by finding high-contrast boundaries.
// Uses gradient magnitude to snap to edges. Click to add fixed points,
// double-click to close the selection.
//
// Modifier conventions match MarqueeTool/LassoTool (evaluated from the
// pointer-down that starts the path):
//   default            → replace
//   shift only         → add (union)
//   alt only           → subtract
//   shift + alt        → intersect
// An explicit `combineMode` set by the options bar's New/Add/Subtract/
// Intersect button strip (audit i60/i162) overrides the modifier-derived
// default; see pickCombine().

import { SetSelectionCommand } from "../commands/SetSelectionCommand";
import { Selection } from "../selection/Selection";
import { screenToDoc } from "../render/Viewport";
import type { KeyModifiers, PointerSample, Tool, ToolContext } from "./Tool";
import type { RasterLayer } from "../document/Layer";
import { findLayer } from "../document/LayerUtils";

export type CombineMode = "replace" | "add" | "subtract" | "intersect";

export interface MagneticLassoOptions {
  readonly id: string;
  readonly shortcut: string;
  width?: number; // search width in doc pixels (default: 10)
  contrast?: number; // edge contrast sensitivity 0..100 (default: 50)
  frequency?: number; // how often to add anchor points 1..100 (default: 50)
}

interface DocPoint {
  x: number;
  y: number;
}

interface Drag {
  readonly originalSelection: Selection | null;
  readonly combine: CombineMode;
  readonly docW: number;
  readonly docH: number;
  readonly points: DocPoint[];
  readonly width: number;
  readonly contrast: number;
  readonly frequency: number;
  movesSinceLastPoint: number;
  lastClickTime: number;
}

export class MagneticLassoTool implements Tool {
  readonly id: string;
  readonly shortcut: string = "L";

  /**
   * Rubber-band a live preview edge between clicks (audit i62) — the host's
   * generic hover-move routing (wantsHoverMove) opts tools into pointermove
   * events even while no drag is in progress by checking this flag.
   */
  readonly tracksHover = true;

  width = 10;
  contrast = 50;
  frequency = 50;
  /** Feather radius in doc pixels, applied to the committed selection (audit i67). */
  feather = 0;
  /** Whether to anti-alias the selection edges (audit i159). */
  antiAlias = true;
  /**
   * Explicit combine-mode override set by the options bar's New/Add/
   * Subtract/Intersect button strip (ToolUX's wireCombineModeButtons,
   * audit i60/i162). When set to "add"/"subtract"/"intersect" it wins over
   * the shift/alt-derived default computed at pointer-down; "new" (or
   * unset) falls back to the existing shift/alt modifier behavior.
   */
  combineMode?: "new" | "add" | "subtract" | "intersect";

  private drag: Drag | null = null;

  constructor(opts: MagneticLassoOptions = { id: "magnetic-lasso", shortcut: "L" }) {
    this.id = opts.id;
    this.shortcut = opts.shortcut;
    if (opts.width !== undefined) this.width = opts.width;
    if (opts.contrast !== undefined) this.contrast = opts.contrast;
    if (opts.frequency !== undefined) this.frequency = opts.frequency;
  }

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc) return;
    const p = screenToDoc(ctx.viewport, s.x, s.y);

    // Double-click detection to close
    const now = Date.now();
    if (this.drag && now - this.drag.lastClickTime < 400) {
      this.closePath(ctx);
      return;
    }

    const docW = ctx.doc.width;
    const docH = ctx.doc.height;

    this.drag = {
      originalSelection: ctx.doc.selection,
      combine: pickCombine(s.shiftKey, s.altKey, this.combineMode),
      docW,
      docH,
      points: [{ x: p.x, y: p.y }],
      width: this.width,
      contrast: this.contrast,
      frequency: this.frequency,
      movesSinceLastPoint: 0,
      lastClickTime: now,
    };
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (!this.drag) return;
    const d = this.drag;
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const last = d.points[d.points.length - 1]!;

    // Find edge in search window
    const edge = this.findEdge(ctx, p, last, d.width, d.contrast);
    if (edge) {
      d.points.push(edge);
      d.movesSinceLastPoint++;

      // Emit preview periodically based on frequency
      const previewInterval = Math.max(1, Math.floor(100 / d.frequency));
      if (d.movesSinceLastPoint >= previewInterval) {
        d.movesSinceLastPoint = 0;
        const local = Selection.fromPolygon(d.docW, d.docH, d.points, this.antiAlias);
        const preview = combineSelection(d.combine, d.originalSelection, local);
        ctx.emitTransient(new SetSelectionCommand(preview));
      }
    }
  }

  onPointerUp(ctx: ToolContext, _s: PointerSample): void {
    // Keep the drag alive — user must double-click or press Enter to close
  }

  onKeyDown(ctx: ToolContext, key: string, modifiers?: KeyModifiers): void {
    if (key === "Enter" && this.drag) {
      this.closePath(ctx);
    }
    if (key === "Escape" && this.drag) {
      this.cancel(ctx);
    }
  }

  private closePath(ctx: ToolContext): void {
    if (!this.drag) return;
    const d = this.drag;
    this.drag = null;

    if (d.points.length < 3) {
      ctx.emit(new SetSelectionCommand(d.originalSelection));
      return;
    }

    const local = Selection.fromPolygon(d.docW, d.docH, d.points, this.antiAlias);
    const combined = combineSelection(d.combine, d.originalSelection, local);
    // Apply feather at commit time (matches MarqueeTool/LassoTool's convention).
    const feathered = combined && this.feather > 0 ? combined.feather(this.feather) : combined;
    ctx.emit(new SetSelectionCommand(feathered));
  }

  cancel(ctx: ToolContext): void {
    if (!this.drag) return;
    const original = this.drag.originalSelection;
    this.drag = null;
    ctx.emitTransient(new SetSelectionCommand(original));
  }

  /**
   * Find the strongest edge within the search window around the current position.
   * Uses gradient magnitude computed from the active raster layer.
   */
  private findEdge(
    ctx: ToolContext,
    current: { x: number; y: number },
    last: DocPoint,
    searchWidth: number,
    contrastSensitivity: number,
  ): DocPoint | null {
    const doc = ctx.doc;
    if (!doc) return null;

    // Get the active raster layer for edge detection
    const layer = this.getActiveRaster(ctx);
    if (!layer) return current;

    const pixels = layer.pixels;
    const pw = pixels.width;
    const ph = pixels.height;
    if (pw <= 0 || ph <= 0) return current;

    // Convert to layer coords
    const lx = Math.floor(current.x - layer.x);
    const ly = Math.floor(current.y - layer.y);

    if (lx < 0 || lx >= pw || ly < 0 || ly >= ph) return current;

    // Search along the direction from last to current, within searchWidth
    const dx = current.x - last.x;
    const dy = current.y - last.y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const nx = -dy / len; // perpendicular
    const ny = dx / len;

    let bestX = lx;
    let bestY = ly;
    let bestGradient = 0;

    const threshold = contrastSensitivity / 100;

    for (let t = -searchWidth; t <= searchWidth; t++) {
      const sx = Math.round(lx + nx * t);
      const sy = Math.round(ly + ny * t);

      if (sx < 1 || sx >= pw - 1 || sy < 1 || sy >= ph - 1) continue;

      // Compute gradient magnitude using Sobel-like operator
      const idx = (sy * pw + sx) * 4;
      const idxL = (sy * pw + sx - 1) * 4;
      const idxR = (sy * pw + sx + 1) * 4;
      const idxU = ((sy - 1) * pw + sx) * 4;
      const idxD = ((sy + 1) * pw + sx) * 4;

      // Use luminance for edge detection
      const lum = (i: number) => {
        const r = pixels.data[i]!;
        const g = pixels.data[i + 1]!;
        const b = pixels.data[i + 2]!;
        return 0.299 * r + 0.587 * g + 0.114 * b;
      };

      const gx = Math.abs(lum(idxR) - lum(idxL));
      const gy = Math.abs(lum(idxD) - lum(idxU));
      const grad = Math.sqrt(gx * gx + gy * gy) / 255;

      if (grad > bestGradient && grad > threshold * 0.3) {
        bestGradient = grad;
        bestX = sx;
        bestY = sy;
      }
    }

    return {
      x: bestX + layer.x,
      y: bestY + layer.y,
    };
  }

  private getActiveRaster(ctx: ToolContext): RasterLayer | null {
    const doc = ctx.doc;
    if (!doc) return null;

    const activeId = doc.activeLayerId;
    if (activeId) {
      const layer = findLayer(doc.layers, activeId);
      if (layer && layer.kind === "raster") return layer;
    }

    // Fall back to top-most raster layer
    for (let i = doc.layers.length - 1; i >= 0; i--) {
      const l = doc.layers[i]!;
      if (l.kind === "raster") return l;
    }
    return null;
  }
}

function pickCombine(shift: boolean, alt: boolean, override?: string): CombineMode {
  // An explicit options-bar combine-mode selection (audit i60/i162) wins over
  // the shift/alt-derived default. "new" (or no override) falls through to
  // the existing modifier-key behavior below.
  if (override === "add" || override === "subtract" || override === "intersect") {
    return override;
  }
  if (shift && alt) return "intersect";
  if (shift) return "add";
  if (alt) return "subtract";
  return "replace";
}

/**
 * Combine the freshly rasterized polygon (`local`) with the document's
 * pre-existing selection according to `mode`. Mirrors LassoTool/
 * PolygonalLassoTool's combine semantics (audit i60/i162 — Magnetic Lasso
 * previously had no combine logic at all and always replaced the selection
 * outright, ignoring both shift/alt and any original selection).
 */
function combineSelection(
  mode: CombineMode,
  original: Selection | null,
  local: Selection,
): Selection | null {
  switch (mode) {
    case "replace":
      return local.isEmpty() ? null : local;
    case "add":
      if (!original) return local.isEmpty() ? null : local;
      if (local.isEmpty()) return original;
      return original.union(local);
    case "subtract":
      if (!original) return null;
      if (local.isEmpty()) return original;
      return original.subtract(local);
    case "intersect":
      if (!original) return null;
      if (local.isEmpty()) return null;
      return original.intersect(local);
  }
}
